/**
 * Shell context — lets a page feed the top bar its breadcrumb tail and an
 * optional workflow-stage indicator without prop-drilling through the router.
 *
 * A page calls `useShellSlot({ breadcrumb, stage })` in an effect; the TopBar
 * reads the current values. Falls back to a path-derived breadcrumb when a page
 * sets nothing.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface ShellSlots {
  /** Trailing breadcrumb segment(s) for the current page, e.g. "Case PFA-1042". */
  breadcrumb?: string;
  /** Optional workflow-stage indicator text shown next to the breadcrumb. */
  stage?: ReactNode;
}

interface ShellContextValue extends ShellSlots {
  setSlots: (slots: ShellSlots) => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<ShellSlots>({});
  const value = useMemo<ShellContextValue>(
    () => ({ ...slots, setSlots }),
    [slots]
  );
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

/** Read the current shell slots (TopBar uses this). */
export function useShellSlots(): ShellSlots & { setSlots: (s: ShellSlots) => void } {
  const ctx = useContext(ShellContext);
  if (!ctx) {
    // Render outside a shell (e.g. login) — no-op.
    return { setSlots: () => {} };
  }
  return ctx;
}

/** A page declares its breadcrumb/stage; cleared on unmount. */
export function useShellSlot(slots: ShellSlots): void {
  const ctx = useContext(ShellContext);
  const { breadcrumb, stage } = slots;
  useEffect(() => {
    ctx?.setSlots({ breadcrumb, stage });
    return () => ctx?.setSlots({});
    // Re-run when the page's declared values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumb, stage]);
}
